import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { config } from '../config/env';

const ACCESS_KEY = 'gf_access_token';
const REFRESH_KEY = 'gf_refresh_token';
const VERTICAL_KEY = 'gf_active_vertical';

export const tokenStore = {
  getAccess: () => localStorage.getItem(ACCESS_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  set: (access: string, refresh: string) => {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear: () => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

/** The active business vertical pinned by the UI switcher — sent as x-vertical-id. */
export const activeVerticalStore = {
  get: (): number | null => {
    const raw = localStorage.getItem(VERTICAL_KEY);
    const id = raw ? Number(raw) : NaN;
    return Number.isInteger(id) && id > 0 ? id : null;
  },
  set: (id: number | null) => {
    if (id === null) localStorage.removeItem(VERTICAL_KEY);
    else localStorage.setItem(VERTICAL_KEY, String(id));
  },
};

export const apiClient = axios.create({
  baseURL: config.apiBaseUrl,
  headers: { 'Content-Type': 'application/json' },
});

// Attach the access token to every request.
apiClient.interceptors.request.use((req: InternalAxiosRequestConfig) => {
  const token = tokenStore.getAccess();
  if (token) req.headers.Authorization = `Bearer ${token}`;
  // For file uploads (FormData), drop the JSON default so the browser sets
  // multipart/form-data with the correct boundary — otherwise multer can't parse it.
  if (typeof FormData !== 'undefined' && req.data instanceof FormData) {
    req.headers.delete('Content-Type');
  }
  // Pin the active business vertical so the backend narrows row-level scope.
  const vertical = activeVerticalStore.get();
  if (vertical !== null) req.headers['x-vertical-id'] = String(vertical);
  return req;
});

// Transparent refresh-on-401 with single-flight queueing.
let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = tokenStore.getRefresh();
  if (!refreshToken) return null;
  try {
    const { data } = await axios.post(`${config.apiBaseUrl}/auth/refresh`, { refreshToken });
    const tokens = data.data;
    tokenStore.set(tokens.accessToken, tokens.refreshToken);
    return tokens.accessToken;
  } catch {
    tokenStore.clear();
    return null;
  }
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      refreshing = refreshing ?? refreshAccessToken();
      const newToken = await refreshing;
      refreshing = null;
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      }
      window.location.assign('/login');
    }
    return Promise.reject(error);
  },
);
